import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, Button, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';

import { useScreenRecorderContext } from '../../hooks/use-screen-recorder-context';

type ScreenShareTesterProps = {
  onScreenShareReady: (ready: boolean) => void;
};

export const ScreenShareTester = ({
  onScreenShareReady,
}: ScreenShareTesterProps) => {
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    isScreenSharingActive,
    acquireScreenStream,
    stopScreenRecording,
    captureInfo,
  } = useScreenRecorderContext();

  const handleStopRecording = useCallback(async () => {
    setError(null);
    await stopScreenRecording();
  }, [stopScreenRecording]);

  const handleStartRecording = useCallback(async () => {
    setIsStarting(true);
    setError(null);

    try {
      await acquireScreenStream();
    } catch (error) {
      console.error('Failed to start screen recording:', error);
      setError('Failed to start screen recording. Please try again.');
    } finally {
      setIsStarting(false);
    }
  }, [acquireScreenStream]);

  const screenShareReady = isScreenSharingActive && !error;

  useEffect(() => {
    onScreenShareReady(screenShareReady);
  }, [screenShareReady, onScreenShareReady]);

  useEffect(() => {
    if (captureInfo && !captureInfo.isCapturingWindowOrMonitor) {
      setError(
        'Please share either your window or entire screen to proceed to the assessment.',
      );
    }
  }, [captureInfo]);

  const getSuccessMessage = () => {
    if (!captureInfo)
      return 'Screen sharing is active! You can now proceed to the interview.';

    if (captureInfo.displaySurface === 'monitor') {
      return "Perfect! You're sharing your entire screen. You can now proceed to the interview.";
    }

    if (captureInfo.displaySurface === 'window') {
      return "Perfect! You're sharing your window. You can now proceed to the interview.";
    }

    return 'You are now sharing your screen. You can now proceed to the interview.';
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant='h6' gutterBottom>
        Screen Share Test
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
        }}
      >
        {/* Success state - screen sharing active and no error */}
        {isScreenSharingActive && !error && (
          <>
            <CheckCircleOutlineIcon
              sx={{
                width: 64,
                height: 64,
                color: 'success.main',
              }}
            />
            <Typography variant='body1' textAlign='center'>
              {getSuccessMessage()}
            </Typography>
          </>
        )}

        {/* Error state - screen sharing active but wrong type */}
        {error && isScreenSharingActive && (
          <>
            <ErrorOutlineIcon
              sx={{
                width: 64,
                height: 64,
                color: 'error.main',
              }}
            />
            <Typography variant='body1' textAlign='center'>
              {error}
            </Typography>
            <Button
              variant='contained'
              onClick={handleStopRecording}
              disabled={isStarting}
              color='primary'
            >
              Stop Screen Sharing
            </Button>
          </>
        )}

        {/* Initial state - no screen sharing */}
        {!isScreenSharingActive && (
          <>
            <Typography variant='body1' textAlign='center'>
              For this interview, you will need to share your screen so the
              interviewer can see your coding session. Click the button below to
              start sharing.
            </Typography>
            <Button
              variant='contained'
              onClick={handleStartRecording}
              disabled={isStarting}
              color='primary'
            >
              {isStarting ? 'Starting...' : 'Start Screen Sharing'}
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
};
